import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import { YoutubePlaylistSelectorOption } from 'containers/YoutubePostModal/YoutubePlaylistSelector';
import useOnChange from 'hooks/useOnChange';
import useSet from 'hooks/useSet';
import { projectNameSelector } from 'redux/modules/download';
import { lastCreatedPlaylistIdSelector } from 'redux/modules/social';
import { YoutubeVideoVisibility } from 'types';
import { formatYouTubeTitle } from './YouTubeComposerStep/utils';

export interface UseYouTubeComposerValuesResult {
  title: string;
  visibility: YoutubeVideoVisibility;
  madeForKids: boolean;
  categoryId: number;
  language: string;
  tags: string[];
  selectedPlaylistsIds: Set<string>;
  onTitleChange: (title: string) => void;
  onPlaylistChange: (option: YoutubePlaylistSelectorOption) => void;
  onVisibilityChange: (visibility: YoutubeVideoVisibility) => void;
  onMadeForKidsChange: (madeForKids: boolean) => void;
  onCategoryIdChange: (categoryId: number) => void;
  onLanguageChange: (language: string) => void;
  onTagsChange: (tags: string[]) => void;
  resetComposerValues: () => void;
}

export default function useYouTubeComposerValues(): UseYouTubeComposerValuesResult {
  const projectName = useSelector(projectNameSelector);

  const [title, setTitle] = useState<string>(formatYouTubeTitle(projectName));
  const [selectedPlaylistsIds, { initialize, toggle, reset }] = useSet<string>(
    new Set([]),
  );
  const [visibility, setVisibility] = useState<YoutubeVideoVisibility>(
    'public',
  );
  const [madeForKids, setMadeForKids] = useState<boolean>(false);
  const [categoryId, setCategoryId] = useState<number>(undefined);
  const [language, setLanguage] = useState<string>(undefined);
  const [tags, setTags] = useState<string[]>([]);

  const { resetDescription } = useSocialShareModal();

  const handleSetDefaultProps = useCallback(() => {
    setTitle(projectName);
    setVisibility('public');
    setMadeForKids(false);
    setCategoryId(undefined);
    setLanguage(undefined);
    setTags([]);
    initialize([]);
    resetDescription();
    reset();
  }, [initialize, projectName, reset, resetDescription]);

  useOnChange(useSelector(lastCreatedPlaylistIdSelector), {
    onChange: playlistId => {
      if (playlistId) {
        toggle(playlistId);
      }
    },
  });

  return {
    title,
    visibility,
    madeForKids,
    categoryId,
    language,
    tags,
    selectedPlaylistsIds,
    onTitleChange: setTitle,
    onPlaylistChange: (option: YoutubePlaylistSelectorOption) =>
      toggle(option.id),
    onVisibilityChange: setVisibility,
    onMadeForKidsChange: setMadeForKids,
    onCategoryIdChange: setCategoryId,
    onLanguageChange: setLanguage,
    onTagsChange: setTags,
    resetComposerValues: handleSetDefaultProps,
  };
}
